import { useEffect, useState } from 'react';
import { Alert, Button, Space, Switch, Typography, message } from 'antd';
import { CheckCircleFilled, SendOutlined } from '@ant-design/icons';
import type { TelegramNotifySettingsResponse } from '@lens/shared';
import { getMyTelegramNotify, setMyTelegramNotify } from './api';
import { SettingRow } from '../profile/ProfileSection.tsx';

const { Text } = Typography;

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/**
 * Личная настройка уведомлений в Telegram в профиле: общий выключатель и
 * выключатель по каждому пространству, где человек участник. Пространство,
 * где уведомления выключил админ, показано серым — от человека там ничего
 * не зависит.
 */
export function TelegramNotifySettings({ botUsername }: { botUsername: string | null }): JSX.Element | null {
  const [state, setState] = useState<TelegramNotifySettingsResponse | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = () =>
      getMyTelegramNotify()
        .then(setState)
        .catch(() => setState(null));
    void load();
    // Человек уходит в Telegram нажать «Start» и возвращается на вкладку —
    // перечитываем, чтобы профиль сразу показал, что бот может писать.
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!state) return null;

  const save = async (enabled: boolean, mutedSpaceIds: string[]) => {
    setSaving(true);
    try {
      setState(await setMyTelegramNotify({ enabled, mutedSpaceIds }));
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить настройку');
    } finally {
      setSaving(false);
    }
  };

  const muted = state.spaces.filter((s) => s.muted).map((s) => s.id);
  // Где админ уведомления не включил, человек ничего поменять не может —
  // такие пространства не показываем списком, только числом.
  const enabledSpaces = state.spaces.filter((s) => s.spaceEnabled);
  const disabledCount = state.spaces.length - enabledSpaces.length;
  const toggleSpace = (spaceId: string, on: boolean) =>
    save(state.enabled, on ? muted.filter((id) => id !== spaceId) : [...muted, spaceId]);

  return (
    <SettingRow
      testId="telegram-notify-settings"
      title="Уведомления"
      hint="Бот пишет вам в личную переписку, когда вас упомянули в комментарии или указали в поле записи."
      aside={
        <Switch
          checked={state.enabled}
          loading={saving}
          onChange={(on) => save(on, muted)}
          data-testid="telegram-notify-master"
          aria-label="Уведомления в Telegram"
        />
      }
    >
      {state.enabled &&
        (state.botChatOpened ? (
          <Space data-testid="telegram-notify-bot-ready">
            <CheckCircleFilled style={{ color: 'var(--color-success)' }} />
            <Text type="secondary">Бот может вам писать</Text>
          </Space>
        ) : (
          <Alert
            type="warning"
            showIcon
            data-testid="telegram-notify-bot-start"
            message="Бот пока не может вам писать"
            description={
              <Space direction="vertical" size={8}>
                <span>
                  Telegram не даёт боту написать первым. Откройте бота и нажмите «Start» — один раз,
                  дальше уведомления будут приходить сами.
                </span>
                {botUsername && (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    href={`https://t.me/${botUsername}?start=notify`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Открыть бота
                  </Button>
                )}
              </Space>
            }
          />
        ))}

      {state.enabled && (
        <div style={{ marginTop: 16 }} data-testid="telegram-notify-spaces">
          {enabledSpaces.length > 0 ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Пространства, где уведомления включены
              </Text>
              {enabledSpaces.map((space) => (
                <div
                  key={space.id}
                  className="tg-notify-space"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-border-secondary, rgba(0,0,0,0.06))',
                  }}
                  data-testid={`telegram-notify-space-${space.slug}`}
                >
                  <Text ellipsis={{ tooltip: space.name }} style={{ flex: 1, minWidth: 0 }}>
                    {space.name}
                  </Text>
                  <Switch
                    size="small"
                    checked={!space.muted}
                    disabled={saving}
                    onChange={(on) => toggleSpace(space.id, on)}
                    style={{ flexShrink: 0 }}
                  />
                </div>
              ))}
            </>
          ) : (
            <Text type="secondary" data-testid="telegram-notify-no-spaces">
              Пока ни в одном вашем пространстве уведомления не включены. Включает их админ
              пространства в настройках, во вкладке «Телеграм».
            </Text>
          )}
          {enabledSpaces.length > 0 && disabledCount > 0 && (
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 8, fontSize: 12 }}
              data-testid="telegram-notify-disabled-count"
            >
              Ещё в {disabledCount} {plural(disabledCount, 'пространстве', 'пространствах', 'пространствах')}{' '}
              уведомления не включены — это решает админ пространства.
            </Text>
          )}
        </div>
      )}
    </SettingRow>
  );
}
